<template>
   <div
      :data-grid-section="gridSection"
      :data-grid-table="gridTableIndex"
      :data-grid-row="gridRowIndex"
      :data-grid-col="gridColIndex"
   >
      <FieldInput
         style="min-width: 6rem; width: 100%; font-size: 12px !important"
         class="mb-0"
         ref="input"
         :ident="id"
         append="%"
         :field="displayPercentageField"
         :disabled="disabled"
         :placeholder="placeholder"
         fill
         @input="onInput"
         @blur="onBlur"
      />
   </div>
</template>

<script>
import {integer} from 'vuelidate/lib/validators';
import FieldInput from '@/components/forms/FieldInput';

export default {
   components: {
      FieldInput,
   },

   props: {
      gridSection: {type: String, required: false},
      gridTableIndex: {type: Number, required: false},
      gridRowIndex: {type: Number, required: false},
      gridColIndex: {type: Number, required: false},

      ident: {
         type: [String, Number],
      },
      assigneeId: {
         type: String,
         default: '',
      },
      summary: {
         type: Object,
         default: null,
      },
      placeholder: {
         type: String,
         default: null,
      },
      disabled: {
         type: Boolean,
         default: false,
      },
      setMany: {
         type: Array,
         default: () => [],
      },
      projectIds: {
         type: Array,
         default: () => [],
      },
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         draftPercentage: null,
         hasDraftPercentage: false,
      };
   },

   computed: {
      id() {
         return `input-percentage-${this.ident}`.replace(/\s/g, '-');
      },

      percentageField() {
         return this.summary?.percentage || null;
      },

      displayPercentageField() {
         if (!this.percentageField) return null;
         return {
            ...this.percentageField,
            value: this.hasDraftPercentage ? this.draftPercentage : this.percentageField.value,
         };
      },
   },

   methods: {
      normalizeInputValue(value) {
         return value === '' || value === null ? null : parseInt(value, 10);
      },

      onInput(value) {
         this.hasDraftPercentage = true;
         this.draftPercentage = value;
         this.validate(value);
      },

      async onBlur(value) {
         const normalizedValue = this.normalizeInputValue(value);
         const currentValue = this.normalizeInputValue(this.percentageField?.value);
         if (!this.hasDraftPercentage && normalizedValue === currentValue) return;
         await this.setPercentage(value);
      },

      async setPercentage(value) {
         if (!this.validate(value)) return;

         // Convert empty string to null and parse numbers
         value = this.normalizeInputValue(value);

         // Confirm clearing all percentages if needed
         if (this.setMany.length > 0 && value === null) {
            const proceed = await this.$bvModal.msgBoxConfirm(
               'Are you sure you want to clear the percentage for every vendor in this account?',
               {
                  centered: true,
                  title: 'Clear all vendors?',
               }
            );
            if (!proceed) return;
         }

         const normalizedSummaries = [this.summary, ...this.setMany].filter(Boolean);

         // Keep local draft synced while store action performs optimistic update.
         this.draftPercentage = value;
         this.hasDraftPercentage = false;

         for (const summary of normalizedSummaries) {
            try {
               await this.$store.dispatch(`${this.expenseType}/savePercentage`, {
                  companyId: this.$companyId,
                  summary: {
                     ...summary,
                     assigneeId: this.assigneeId,
                  },
                  percentage: value,
               });
            } catch {
               // The store action handles rollback and user alerting.
            }
         }
      },

      /** Validate a percentage */
      validate(value) {
         if (value === '' || value === null) {
            this.$refs.input.setState(null);
            return true;
         }
         if (!integer(value)) {
            this.$refs.input.setState(false, 'Must be an integer');
            return false;
         }
         value = parseInt(value, 10);
         if (value < 0 || value > 100) {
            this.$refs.input.setState(false, 'Must be between 0 and 100');
            return false;
         }
         this.$refs.input.setState(null);
         return true;
      },
   },
};
</script>

<style lang="scss" scoped></style>
